<template>
  <div class="form-item-container" :class="{ 'first-form-item': form_item_order === 0 && layout_mode === 'all_on_one_page' }">
    <div class="questions-container">
      <div class="question-primary-language">
        {{ question_lang1 }}
      </div>
      <div class="question-secondary-language" v-if="bilingue">
        {{ question_lang2 }}
      </div>
    </div>
    <div class="response-container-two-options" v-if="options.length === 2">
      <div class="option-container" v-for="(option, index) in options" :key="index" @click="handleOptionClick(index)" :class="{ 'clicked': clickedOption === index }">
        <span class="option-primary-language">{{ option.text_lang1 }}</span>
        <span class="option-secondary-language" v-if="bilingue">{{ option.text_lang2 }}</span>
      </div>
    </div>
    <div class="response-container-multi-options" v-if="options.length > 2">
      <div v-for="(option, index) in options" :key="index" @click="handleOptionClick(index)" class="option-container">
        <div class="option-id" :class="{ 'clicked': clickedOption === index }">
          <span>{{ optionsLetter[index] }}</span>
        </div>
        <div class="option-text">
          <span class="option-primary-language">{{ option.text_lang1 }}</span>
          <span class="option-secondary-language" v-if="bilingue">{{ option.text_lang2 }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { FormItemOption } from '@/utils/DataTypes'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'MultiChoice',
  props: {
    question_lang1: {
      type: String,
      default: ''
    },
    question_lang2: {
      type: String,
      default: ''
    },
    bilingue: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array as () => FormItemOption[],
      default: () => []
    },
    form_item_id: {
      type: Number,
      default: 0
    },
    form_item_order: {
      type: Number,
      default: -1
    },
    layout_mode: {
      type: String,
      default: 'all_on_one_page'
    }
  },
  data() {
    return {
      clickedOption: null as number | null,
      optionsLetter: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O']
    }
  },
  methods: {
    handleOptionClick(index: number) {
      this.clickedOption = index
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';

.form-item-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  width: 100%;
  padding: 2em 5% 2em 5%;
  color: $gorate-secondary;

  .questions-container {
    grid-row: 1;

    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;

    align-items: center;
    justify-items: center;

    .question-primary-language {
      grid-row: 1;
      font-size: clamp(1.5em, 1vw + 1rem, 2em);
      font-weight: bold;
      padding-bottom: 0.2em;
      text-align: center;
    }

    .question-secondary-language {
      grid-row: 2;
      font-size: clamp(1em, 0.5vw + 1rem, 1.5em);
      text-align: center;
    }
  }

  .response-container-two-options {
    grid-row: 2;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1em;

    padding: 1em;

    .option-container {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;

      justify-content: center;

      padding: 1em 2em;

      border: 1px solid white;
      border-radius: 10px;
      transition: background-color 0.3s ease;
      transition: color 0.3s ease;

      cursor: pointer;

      background-color: $gorate-medium;
      border-radius: 23px;
      border: 4px solid transparent;
      color: #000000;

      .option-primary-language {
        width: 100%;
        font-size: clamp(1.2em, 1vw + 1rem, 1.5em);
        font-weight: bold;
        text-align: center;

        align-self: center;
        align-items: center;
      }

      .option-secondary-language {
        width: 100%;
        font-size: clamp(1.2em, 0.5vw + 1rem, 1.5em);
        text-align: center;

      }

      &.clicked {
        background-color: $gorate-secondary !important;
        border: 4px solid $gorate-light !important;
        color: #fff !important;
      }
    }
  }

  .response-container-multi-options {
    grid-row: 2;

    display: flex;
    flex-direction: column;

    width: 100%;
    padding: 1em;

    gap: 20px;
    line-height: 40px;
    font-size: 20px;
    justify-self: center;

    .option-container {
      display: grid;
      grid-template-columns: 15% 1fr;
      border-radius: 29px;
      border: 4px solid transparent;

      .option-id {
        grid-column: 1;
        width: 100%;
        background-color: $gorate-secondary;
        transition: background-color 0.3s ease;
        transition: color 0.3s ease;
        display: flex;
        align-items: center;
        border-bottom-left-radius: 23px;
        border-top-left-radius: 23px;
        justify-content: center;
        font-size: 2em;
        font-weight: bold;
        color: #fff;
        padding: 1rem;
      }

      .option-text {
        grid-column: 2;
        cursor: pointer;

        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        width: 100%;
        border-radius: 0px 23px 23px 0px;

        background-color: $gorate-medium;
        transition: background-color 0.3s ease;
        transition: color 0.3s ease;

        padding: 1rem 2rem;
        align-items: center;

        .option-primary-language {
          width: 100%;
          color: #000;
          font-size: clamp(1.2em, 1vw + 1rem, 1.5em);
          text-align: start;
          font-weight: bold;

          align-self: center;
          align-items: center;
        }

        .option-secondary-language {
          width: 100%;
          color: #000;
          font-size: clamp(1em, 0.5vw + 1rem, 1.5em);
          text-align: start;

        }
      }
    }

    .option-container:has(.option-id.clicked) {
      border: 4px solid $gorate-secondary;
    }
  }
}

.form-item-container.first-form-item {
  .response-container-multi-options {
    .option-container {
      .option-id {
        background-color: #fff !important;
        color: #000 !important;
      }
    }

    .option-container:has(.option-id.clicked) {
      .option-id {
        background-color: $gorate-secondary !important;
        color: #fff !important;
      }

      border: 4px solid $gorate-light !important;
    }
  }
}
</style>
